// An area's site requirements: access, hazard and condition tags and the
// preliminaries, ticked on site (SCHEMA.md "Before UAT" §1, §3), a row per
// tick (lib/tick-rows.ts). The list comes from CRM (hig_accesstag) and is kept
// on the device.
import { useState } from 'react';
import { Check, X } from 'lucide-react';
import { addOther, removeOther, retiredTicks, tagsForSide, toggleTag, type AccessTag, type SiteReqsDoc } from '@higgins/m2q-pricing';
import { listRecords, type DeviceRecord } from '../lib/device-db';
import { useDeviceQuery } from '../lib/hooks';
import { loadSiteRequirementTags } from '../lib/reference';
import { areaSiteReqs, areaTickOwner, asTickRow, canWriteTicks, saveTicks, tickRowsOf, untickRow, type TickOwner } from '../lib/tick-rows';
import { AddAction } from './add-action';
import { Notice } from './message';

interface SiteRequirementsProps {
  area: DeviceRecord;
  /** The area's side: Internal / External categories show only on areas of that side. */
  side: string | null;
}

function readDoc(records: DeviceRecord[], area: DeviceRecord, tags: AccessTag[]): { doc: SiteReqsDoc } | { error: string } {
  try {
    return { doc: areaSiteReqs(records, area, tags) };
  } catch (err) {
    return { error: err instanceof Error ? err.message : String(err) };
  }
}

const chip = (on: boolean) =>
  `flex min-h-11 items-center gap-1 rounded-full border px-3 text-sm ${on ? 'border-primary bg-orange-50 text-orange-900' : 'border-slate-300 bg-white text-slate-700'}`;

/** A free-text entry for one category — "Crane hire" under Preliminary. */
function OtherInput({ category, onAdd }: { category: string; onAdd: (label: string) => void }) {
  const [text, setText] = useState('');
  const add = () => {
    onAdd(text);
    setText('');
  };
  return (
    <span className="flex items-center gap-1">
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            add();
          }
        }}
        enterKeyHint="done"
        placeholder="Other…"
        aria-label={`Other ${category}`}
        className="h-11 w-36 rounded-full border border-dashed border-slate-300 px-3 text-sm"
      />
      {text.trim() && (
        <AddAction onClick={add} className="px-2">
          Add
        </AddAction>
      )}
    </span>
  );
}

function Category({ category, tags, doc, onChange }: { category: string; tags: AccessTag[]; doc: SiteReqsDoc; onChange: (doc: SiteReqsDoc) => void }) {
  const others = doc.other.filter((o) => o.category === category);
  const ticked = tags.filter((t) => doc.tags.includes(t.key)).length + others.length;
  return (
    <details className="rounded-lg border border-slate-200 bg-white" open={ticked > 0}>
      <summary className="flex min-h-11 cursor-pointer items-center justify-between px-3 text-sm font-medium text-slate-800">
        {category}
        {ticked > 0 && <span className="rounded-full bg-orange-100 px-2 text-xs text-orange-900">{ticked}</span>}
      </summary>
      <div className="flex flex-wrap gap-2 border-t border-slate-100 p-3">
        {tags.map((t) => {
          const on = doc.tags.includes(t.key);
          return (
            <button key={t.key} type="button" aria-pressed={on} onClick={() => onChange(toggleTag(doc, t.key))} className={chip(on)}>
              {on && <Check className="h-3.5 w-3.5" />} {t.label}
            </button>
          );
        })}
        {others.map((o) => (
          <span key={o.label} className={chip(true)}>
            {o.label}
            <button type="button" onClick={() => onChange(removeOther(doc, category, o.label))} aria-label={`Remove ${o.label}`} className="ml-1 text-slate-500">
              <X className="h-3.5 w-3.5" />
            </button>
          </span>
        ))}
        <OtherInput category={category} onAdd={(label) => onChange(addOther(doc, category, label))} />
      </div>
    </details>
  );
}

/** Ticks no longer in CRM's list: kept on the area, shown so they can be removed. */
function Retired({ items }: { items: { label: string; remove: () => void }[] }) {
  if (!items.length) return null;
  return (
    <p className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
      No longer in the list:
      {items.map((item) => (
        <button key={item.label} type="button" onClick={item.remove} className="rounded-full border border-slate-300 px-2 py-1 line-through">
          {item.label}
        </button>
      ))}
    </p>
  );
}

/** The retired ticks: rows whose tag left the list, or — on an area still on the JSON — keys not in it. */
function retiredItems(records: DeviceRecord[], owner: TickOwner, doc: SiteReqsDoc, tags: AccessTag[], save: (doc: SiteReqsDoc) => void) {
  if (!tags.length) return [];
  const rows = tickRowsOf(records, 'areatag', owner.parent.id).map((record) => ({ ...asTickRow(record), record }));
  if (rows.length) return retiredTicks(rows, tags).map((r) => ({ label: r.label, remove: () => void untickRow(owner, r.record) }));
  return doc.tags.filter((k) => !tags.some((t) => t.key === k)).map((k) => ({ label: k, remove: () => save(toggleTag(doc, k)) }));
}

export function SiteRequirements({ area, side }: SiteRequirementsProps) {
  const data = useDeviceQuery(async () => ({ tags: await loadSiteRequirementTags(), records: await listRecords(area.jobId) }), [area.jobId]);
  if (!data) return null;
  const { tags, records } = data;
  const stored = records.find((r) => r.id === area.id) ?? area;
  const owner = areaTickOwner(records, stored);
  const read = readDoc(records, stored, tags);
  if ('error' in read) return <Notice>{read.error} Update the app to see and edit them.</Notice>;
  if (tags.length === 0 || !canWriteTicks(tags) || !owner) {
    return <p className="text-sm text-slate-600">The access and hazard list isn&rsquo;t up to date on this device yet — open a job while you have signal.</p>;
  }
  const save = (doc: SiteReqsDoc) => void saveTicks(owner, read.doc, doc, tags);
  return (
    <div className="space-y-2">
      {tagsForSide(tags, side).map((g) => (
        <Category key={g.category} category={g.category} tags={g.tags} doc={read.doc} onChange={save} />
      ))}
      <Retired items={retiredItems(records, owner, read.doc, tags, save)} />
    </div>
  );
}
